// src/features/weekly-one/WeeklyOneContext.tsx
import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { supabase } from '@/lib/supabase/client';
import { useAuth } from '@/features/auth/AuthContext';
import { useUserRole } from '@/hooks/useUserRole';
import {
  getCTWeekStart,
  getCTWeekEnd,
  getDaysUntilCutoff,
  deriveCardState,
  type WeeklyCommitment,
  type CardState,
} from '@/features/weekly-one/weeklyOneUtils';

export type { WeeklyCommitment, CardState };

interface WeeklyOneContextValue {
  loading: boolean;
  currentCommitment: WeeklyCommitment | null;
  lastWeekCommitment: WeeklyCommitment | null;
  streakCount: number;
  cardState: CardState;
  daysUntilCutoff: number;
  // Modals
  setModalOpen: boolean;
  doneModalOpen: boolean;
  openSetModal: () => void;
  openDoneModal: () => void;
  closeSetModal: () => void;
  closeDoneModal: () => void;
  // Last-week review
  needsLastWeekReview: boolean;
  resolveLastWeekCommitment: (outcome: 'completed' | 'missed') => Promise<void>;
  // Actions
  saveCommitment: (text: string, offersLastWeek: number, blockerText: string, lastWeekOutcome?: 'completed' | 'partial' | 'missed') => Promise<void>;
  editCommitment: (text: string, offersLastWeek: number, blockerText: string) => Promise<void>;
  markComplete: () => Promise<void>;
  markIncomplete: () => Promise<void>;
  markMissed: () => Promise<void>;
  markPartial: () => Promise<void>;
  saveBlocker: (text: string) => Promise<void>;
}

const WeeklyOneContext = createContext<WeeklyOneContextValue | null>(null);

export function WeeklyOneProvider({ children }: { children: ReactNode }) {
  const { session } = useAuth();
  const { isMember } = useUserRole();
  const userId = session?.user?.id;

  const [loading, setLoading] = useState(true);
  const [currentCommitment, setCurrentCommitment] = useState<WeeklyCommitment | null>(null);
  const [lastWeekCommitment, setLastWeekCommitment] = useState<WeeklyCommitment | null>(null);
  const [streakCount, setStreakCount] = useState(0);
  const [setModalOpen, setSetModalOpen] = useState(false);
  const [doneModalOpen, setDoneModalOpen] = useState(false);

  const weekStart = getCTWeekStart();
  const weekEnd = getCTWeekEnd();

  // Previous week's Monday
  const prevWeekStart = (() => {
    const [y, m, d] = weekStart.split('-').map(Number);
    const prev = new Date(Date.UTC(y, m - 1, d - 7));
    return prev.toISOString().slice(0, 10);
  })();

  const fetchData = useCallback(async () => {
    if (!userId || !isMember) { setLoading(false); return; }
    setLoading(true);

    const [currentRes, lastRes, profileRes] = await Promise.all([
      supabase
        .from('weekly_commitments')
        .select('*')
        .eq('user_id', userId)
        .eq('week_start', weekStart)
        .maybeSingle(),
      supabase
        .from('weekly_commitments')
        .select('*')
        .eq('user_id', userId)
        .eq('week_start', prevWeekStart)
        .maybeSingle(),
      supabase
        .from('profiles')
        .select('streak_count')
        .eq('user_id', userId)
        .maybeSingle(),
    ]);

    setCurrentCommitment(currentRes.data as WeeklyCommitment | null);
    setLastWeekCommitment(lastRes.data as WeeklyCommitment | null);
    setStreakCount((profileRes.data as { streak_count: number } | null)?.streak_count ?? 0);
    setLoading(false);
  }, [userId, isMember, weekStart, prevWeekStart]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const cardState: CardState = deriveCardState(currentCommitment, lastWeekCommitment);
  const days = getDaysUntilCutoff();

  const needsLastWeekReview = !!(lastWeekCommitment && lastWeekCommitment.status === 'active');

  // ---------------------------------------------------------------------------
  // Event logging
  // ---------------------------------------------------------------------------
  const logEvent = useCallback(async (eventType: string, properties: Record<string, unknown> = {}) => {
    if (!userId) return;
    await supabase.from('member_events').insert({ user_id: userId, event_type: eventType, properties });
  }, [userId]);

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------
  const saveCommitment = useCallback(async (text: string, offersLastWeek: number, blockerText: string, lastWeekOutcome?: 'completed' | 'partial' | 'missed') => {
    if (!userId) return;

    // Update prior week's commitment status if the member reported an outcome
    if (lastWeekOutcome && lastWeekCommitment?.status === 'active') {
      const now = new Date().toISOString();
      await supabase
        .from('weekly_commitments')
        .update({
          status: lastWeekOutcome,
          ...(lastWeekOutcome === 'completed' ? { completed_at: now } : {}),
          updated_at: now,
        })
        .eq('user_id', userId)
        .eq('week_start', prevWeekStart);
      await logEvent('weekly_one.last_week_outcome', {
        week_start: prevWeekStart,
        outcome: lastWeekOutcome,
      });
    }

    const { error } = await supabase.from('weekly_commitments').insert({
      user_id: userId,
      week_start: weekStart,
      week_end: weekEnd,
      commitment_text: text.trim(),
      offers_submitted_last_week: offersLastWeek,
      blocker_text: blockerText.trim() || null,
      status: 'active',
    });
    if (error) throw new Error(error.message);
    await logEvent('weekly_one.commitment_set', {
      week_start: weekStart,
      commitment_text: text.trim(),
      offers_submitted_last_week: offersLastWeek,
      blocker_text: blockerText.trim() || null,
    });
    await fetchData();
  }, [userId, weekStart, weekEnd, prevWeekStart, lastWeekCommitment, logEvent, fetchData]);

  const editCommitment = useCallback(async (text: string, offersLastWeek: number, blockerText: string) => {
    if (!userId || !currentCommitment) return;
    const { error } = await supabase
      .from('weekly_commitments')
      .update({
        commitment_text: text.trim(),
        offers_submitted_last_week: offersLastWeek,
        blocker_text: blockerText.trim() || null,
        updated_at: new Date().toISOString(),
      })
      .eq('user_id', userId)
      .eq('week_start', weekStart);
    if (error) throw new Error(error.message);
    await logEvent('weekly_one.commitment_edited', {
      week_start: weekStart,
      commitment_text: text.trim(),
      offers_submitted_last_week: offersLastWeek,
      blocker_text: blockerText.trim() || null,
    });
    await fetchData();
  }, [userId, currentCommitment, weekStart, logEvent, fetchData]);

  const markComplete = useCallback(async () => {
    if (!userId || !currentCommitment) return;
    const now = new Date().toISOString();
    const { error } = await supabase
      .from('weekly_commitments')
      .update({ status: 'completed', completed_at: now, updated_at: now })
      .eq('user_id', userId)
      .eq('week_start', weekStart);
    if (error) throw new Error(error.message);
    await logEvent('weekly_one.marked_complete', { week_start: weekStart, completed_at: now });
    await fetchData();
  }, [userId, currentCommitment, weekStart, logEvent, fetchData]);

  const markIncomplete = useCallback(async () => {
    if (!userId || !currentCommitment) return;
    const { error } = await supabase
      .from('weekly_commitments')
      .update({ status: 'active', completed_at: null, updated_at: new Date().toISOString() })
      .eq('user_id', userId)
      .eq('week_start', weekStart);
    if (error) throw new Error(error.message);
    await logEvent('weekly_one.marked_incomplete', { week_start: weekStart });
    await fetchData();
  }, [userId, currentCommitment, weekStart, logEvent, fetchData]);

  const markMissed = useCallback(async () => {
    if (!userId || !currentCommitment) return;
    const { error } = await supabase
      .from('weekly_commitments')
      .update({ status: 'missed', updated_at: new Date().toISOString() })
      .eq('user_id', userId)
      .eq('week_start', weekStart);
    if (error) throw new Error(error.message);
    await logEvent('weekly_one.marked_missed', { week_start: weekStart });
    await fetchData();
  }, [userId, currentCommitment, weekStart, logEvent, fetchData]);

  const markPartial = useCallback(async () => {
    if (!userId || !currentCommitment) return;
    const { error } = await supabase
      .from('weekly_commitments')
      .update({ status: 'partial', updated_at: new Date().toISOString() })
      .eq('user_id', userId)
      .eq('week_start', weekStart);
    if (error) throw new Error(error.message);
    await logEvent('weekly_one.marked_partial', { week_start: weekStart });
    await fetchData();
  }, [userId, currentCommitment, weekStart, logEvent, fetchData]);

  const resolveLastWeekCommitment = useCallback(async (outcome: 'completed' | 'missed') => {
    if (!userId || !lastWeekCommitment || lastWeekCommitment.status !== 'active') return;
    const now = new Date().toISOString();
    const { error } = await supabase
      .from('weekly_commitments')
      .update({
        status: outcome,
        ...(outcome === 'completed' ? { completed_at: now } : {}),
        updated_at: now,
      })
      .eq('user_id', userId)
      .eq('week_start', prevWeekStart);
    if (error) throw new Error(error.message);
    await logEvent('weekly_one.last_week_resolved', { week_start: prevWeekStart, outcome });
    await fetchData();
  }, [userId, lastWeekCommitment, prevWeekStart, logEvent, fetchData]);

  const saveBlocker = useCallback(async (text: string) => {
    if (!userId || !currentCommitment) return;
    const { error } = await supabase
      .from('weekly_commitments')
      .update({ blocker_text: text.trim() || null, updated_at: new Date().toISOString() })
      .eq('user_id', userId)
      .eq('week_start', weekStart);
    if (error) throw new Error(error.message);
    // No separate event log for blocker saves (too noisy)
    await fetchData();
  }, [userId, currentCommitment, weekStart, fetchData]);

  return (
    <WeeklyOneContext.Provider
      value={{
        loading,
        currentCommitment,
        lastWeekCommitment,
        streakCount,
        cardState,
        daysUntilCutoff: days,
        setModalOpen,
        doneModalOpen,
        openSetModal: () => setSetModalOpen(true),
        openDoneModal: () => setDoneModalOpen(true),
        closeSetModal: () => setSetModalOpen(false),
        closeDoneModal: () => setDoneModalOpen(false),
        needsLastWeekReview,
        resolveLastWeekCommitment,
        saveCommitment,
        editCommitment,
        markComplete,
        markIncomplete,
        markMissed,
        markPartial,
        saveBlocker,
      }}
    >
      {children}
    </WeeklyOneContext.Provider>
  );
}

// Standard React Context pattern -- Provider + hook in the same file.
// eslint-disable-next-line react-refresh/only-export-components
export function useWeeklyOne(): WeeklyOneContextValue {
  const ctx = useContext(WeeklyOneContext);
  if (!ctx) throw new Error('useWeeklyOne must be used within WeeklyOneProvider');
  return ctx;
}
